import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Trash2, Upload, Ticket, Info } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { Badge } from '@/components/ui/Badge'
import { InfoPopover } from '@/components/ui/InfoPopover'
import { StatusDot } from '@/components/ui/StatusDot'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useCrudList } from '@/hooks/useCrudList'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { toast } from '@/components/ui/Toast'
import { CollaboratorsService, type Collaborator } from '@/services/modules/CollaboratorsService'
import { MyCompanyService } from '@/services/modules/MyCompanyService'
import { EmployeeImportModal } from '@/components/EmployeeImportModal'
import { useAuthStore } from '@/stores/authStore'

export function MyCompanyCollaboratorsPage() {
  const navigate = useNavigate()
  // Esta tela é acessível pela EMPRESA e também pela administração. Alguns dados
  // (ex.: quando o colaborador foi inativado) são só para a administração.
  const userType = useAuthStore((s) => s.user?.type)
  const isStaff = userType === 'ADMINISTRADOR' || userType === 'COLABORADOR'
  const [reload, setReload] = useState(0)
  const sf = useStatusFilter({ searchPlaceholder: 'Buscar colaborador' })
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(CollaboratorsService, { size: 15, reloadKey: reload, filters: sf.filters })
  const [deleting, setDeleting] = useState<Collaborator | null>(null)
  const [importOpen, setImportOpen] = useState(false)

  async function handleDelete() {
    if (!deleting) return
    try { await CollaboratorsService.destroy(deleting.id); toast.success('Colaborador removido.'); setDeleting(null); setReload(v => v + 1) }
    catch { toast.error('Erro ao remover.') }
  }

  const columns: DataTableColumn<Collaborator>[] = [
    { key: 'name', header: 'Colaborador', alwaysVisible: true, render: c => (
      <div className="flex items-center gap-3">
        <InfoPopover align="left" ariaLabel="Detalhes do colaborador" title="Detalhes"
          trigger={<Info size={16} className="text-gray-400 hover:text-brand-500 transition" />}>
          <dl className="space-y-2 text-xs">
            <InfoRow label="Plano" value={c.plan?.name || '—'} />
            <InfoRow label="Filial" value={c.companyBranch?.systemName || c.companyBranch?.corporateName || '—'} />
            <InfoRow label="Carteirinha" value={c.formatted?.membershipCardNumber || c.membershipCardNumber || '—'} />
            {/* Data da inativação: informação de gestão — só para a administração,
                não para o usuário EMPRESA. */}
            {isStaff && c.status === 'INATIVO' && (
              <InfoRow label="Inativado em" value={c.formatted?.inactivatedAt || 'Data não registrada'} />
            )}
          </dl>
        </InfoPopover>
        <div className="min-w-0">
          <div className="font-medium text-gray-800">{c.name}</div>
          <div className="text-[11px] text-gray-500">{c.formatted?.cpf ?? c.cpf ?? '—'}</div>
        </div>
      </div>
    ) },
    { key: 'plan', header: 'Plano', render: c => <span className="text-gray-700">{c.plan?.name ?? '—'}</span> },
    { key: 'deps', header: 'Dependentes', align: 'center', className: 'w-28', render: c => <Badge variant="gray">{c.dependents?.length ?? 0}</Badge> },
    { key: 'branch', header: 'Filial', render: c => <span className="text-gray-600">{c.companyBranch?.systemName ?? '—'}</span> },
    { key: 'status', header: 'Status', align: 'center', className: 'w-24', render: c => <StatusDot value={c.status} /> },
  ]

  return (
    <>
      <PageHeader title="Colaboradores" subtitle="Titulares vinculados à empresa"
        crumbs={[{ label: 'Minha empresa' }, { label: 'Colaboradores' }]}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => setImportOpen(true)} iconLeft={<Upload size={14} />}>Importar funcionários</Button>
            <Button variant="secondary" onClick={() => navigate('/my-company/collaborators/aplicar-cupom')} iconLeft={<Ticket size={14} />}>Aplicar cupom em lote</Button>
            <Button onClick={() => navigate('/my-company/collaborators/novo')} iconLeft={<Plus size={14} />}>Novo colaborador</Button>
          </div>
        }
      />
      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="mycompany.collaborators"
        columns={columns} rows={rows} loading={loading} getRowKey={r => r.id}
        emptyMessage="Nenhum colaborador cadastrado."
        rowActions={(c) => [
          { key: 'edit', label: 'Editar', icon: <Pencil size={14} />, onClick: () => navigate(`/my-company/collaborators/${c.id}/editar`) },
          { key: 'delete', label: 'Excluir', icon: <Trash2 size={14} />, danger: true, divider: true, onClick: () => setDeleting(c) },
        ]}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <ConfirmDialog open={!!deleting} onClose={() => setDeleting(null)} onConfirm={handleDelete}
        title="Remover colaborador?" description={`Remover "${deleting?.name}" e seus dependentes?`} confirmLabel="Excluir" variant="danger" />

      {importOpen && (
        <EmployeeImportModal
          title="Importar funcionários"
          onClose={() => setImportOpen(false)}
          onImported={() => setReload(v => v + 1)}
          downloadTemplate={() => MyCompanyService.importTemplate()}
          preview={(f) => MyCompanyService.importPreview(f)}
          confirm={(f) => MyCompanyService.importConfirm(f)}
        />
      )}
      {sf.drawer}
    </>
  )
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-gray-800 font-medium text-right">{value}</dd>
    </div>
  )
}
